import { Controller } from '@hotwired/stimulus'
import { useCollectionEventListener } from 'stimulus-library'

/**
 * This stimulus controller is intended to be used with invalid form inputs, to remove the
 * invalid state indicators when the user changes the input value.
 *
 * It is meant to manage a collection of form groups (as rendered by simple form, with the 'form-group' class).
 * The target fields can be identified two different ways:
 *  1. fields within the 'managedFormGroup' stimulus target elements
 *  2. fields within the controller element
 *    - if no 'managedFormGroup' targets are provided, the controller element is expected to have
 *      the 'form-group' class
 *
 * It will monitor inputs for changes and remove the error states from the form groups:
 *  - this monitors all descendant inputs of the controller's element for changes. It then removes
 *    error states _only_ from inputs within the managed form groups. Note that the set of all
 *    descendant inputs may not be the same as the set of inputs within the managed form groups.
 */
export default class InvalidFieldController extends Controller {
  /**
   * Output -- the error state of the these form group targets will be affected
   */
  static targets = ['managedFormGroup']

  connect() {
    super.connect()
    useCollectionEventListener(this, this.monitoredInputs(), 'input', this.assumeFieldsAreValid)
  }

  assumeFieldsAreValid() {
    this.hideErrorMessages()
    this.removeFieldsErrorState()
  }

  removeFieldsErrorState() {
    if (this.managedFormGroups().length === 0) return

    this.managedFormGroups().forEach(formGroup => {
      formGroup.classList.remove('form-group--error')

      const fields = formGroup.querySelectorAll(this.inputSelector())
      fields.forEach(field => {
        const wasInvalid = field.getAttribute('aria-invalid') === 'true'
        field.setAttribute('aria-invalid', 'false')
        if (wasInvalid) field.dispatchEvent(new Event('input', { bubbles: true }))
      })
    })
  }

  monitoredInputs() {
    return Array.from(this.element.querySelectorAll(this.inputSelector()))
  }

  inputSelector() { return 'input, select, textarea' }

  managedFormGroups() {
    if (!this._managedFormGroups) {
      if (this.hasManagedFormGroupTarget) {
        this._managedFormGroups = this.managedFormGroupTargets
      } else if (this.element.classList.contains('form-group')) {
        this._managedFormGroups = [this.element]
      } else {
        console.error(`${this.constructor.name}: managedFormGroups not found`)
        this._managedFormGroups = []
      }
    }
    return this._managedFormGroups
  }

  hideErrorMessages() {
    this.errorMessageElements().forEach(errorMessageElement => {
      if (errorMessageElement.hasAttribute('hidden')) return

      errorMessageElement.setAttribute('hidden', 'true')
    })
  }

  errorMessageElements() {
    if (!this._errorMessageElements) {
      this._errorMessageElements = this.managedFormGroups()
        .map(element => element.querySelector('.form-error'))
        .filter(e => e)
    }
    return this._errorMessageElements
  }
}
